import React from 'react'
import packageJson from '../../../package.json'
import { useNavigate } from 'react-router-dom'
import { Avatar, Divider, Icon, IconButton, List, ListItemButton, ListItemIcon, ListItemText, Popover, Typography, Box } from '@mui/material'
import { AuthContext } from '../../contexto'
import { USER_PATH, CHANGE_PASSWORD_PATH } from '../../paths/paths'
import useAuthContext from '../../modules/auth/hook/useAuthContext'

interface Props {
  compact?: boolean
  onLogout: () => void
}

// Paleta limpia que se mimetiza con la nueva barra lateral blanca
const UI = {
  bg: '#ffffff',
  border: '#edf2ff',
  text: '#2d3748',        // Gris oscuro para legibilidad premium
  textMuted: '#718096',   // Gris intermedio para el rol
  hover: '#f7fafc',       // Efecto hover muy sutil
  iconColor: '#4a5568',
}

const UserMenu = (props: Props) => {
  // Contexto
  const { user, logout } = useAuthContext()

  // Hooks
  const navigate = useNavigate()
  const [anchorEl, setAnchorEl] = React.useState<HTMLButtonElement | null>(null)

  // Handlers
  const handleLogout = () => { setAnchorEl(null); props.onLogout(); logout() }
  const handleChangePassword = () => { setAnchorEl(null); navigate(USER_PATH + CHANGE_PASSWORD_PATH) }

  // Varios
  const userInitial = (user?.nombre || '').charAt(0).toUpperCase()

  return (
    <React.Fragment>
      {Boolean(user) && (
        <React.Fragment>
          {/* Contenedor Principal en la Barra Lateral */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: props.compact ? 'center' : 'space-between',
              width: '100%',
              backgroundColor: 'transparent',
              p: props.compact ? 0 : 0.5,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
              {/* Avatar circular con color de tu paleta principal o foto si existiera */}
              <Avatar
                sx={{
                  width: 40,
                  height: 40,
                  backgroundColor: 'primary.main',
                  fontWeight: 700,
                  fontSize: 15,
                  color: 'white',
                  boxShadow: '0 2px 8px rgba(124, 94, 194, 0.25)'
                }}
              >
                {userInitial}
              </Avatar>

              {/* Textos: Nombre y Rol (Se ocultan si el menú lateral está compacto/colapsado) */}
              {!props.compact && (
                <Box sx={{ display: 'flex', flexDirection: 'col', minWidth: 0 }}>
                  <Typography
                    noWrap
                    sx={{ fontSize: '13.5px', fontWeight: 600, color: UI.text, lineHeight: 1.3 }}
                  >
                    {user?.nombre}
                  </Typography>
                  <Typography
                    noWrap
                    sx={{ fontSize: '11px', color: UI.textMuted, fontWeight: 500 }}
                  >
                    {user?.rol || 'Personal'}
                  </Typography>
                </Box>
              )}
            </Box>

            {/* Botón de despliegue */}
            {!props.compact && (
              <IconButton
                onClick={(e) => setAnchorEl(e.currentTarget)}
                size="small"
                sx={{ color: UI.textMuted, '&:hover': { backgroundColor: UI.hover } }}
              >
                <Icon sx={{ fontSize: '20px' }}>more_vert</Icon>
              </IconButton>
            )}

            {/* Si está compacto, el mismo avatar abre el menú al hacerle clic */}
            {props.compact && (
              <IconButton
                onClick={(e) => setAnchorEl(e.currentTarget)}
                sx={{ p: 0 }}
              >
                {/* Reutiliza el trigger de arriba */}
              </IconButton>
            )}
          </Box>

          {/* MENÚ DESPLEGABLE (POPOVER) ESTILO CLINIVA */}
          <Popover
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
            transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            onClose={() => setAnchorEl(null)}
            slotProps={{
              paper: {
                sx: {
                  backgroundColor: "#ffffff",
                  color: UI.text,
                  border: "1px solid #e2e8f0",
                  borderRadius: 3,
                  boxShadow:
                    "0 10px 25px -5px rgba(0,0,0,0.05), 0 8px 10px -6px rgba(0,0,0,0.05)",
                  minWidth: 220,
                  mt: "-10px",
                },
              },
            }}
          >
            {/* Cabecera interna del popover rápida */}
            <Box sx={{ px: 2, py: 1.5, backgroundColor: '#f8fafc' }}>
              <Typography sx={{ fontSize: '12px', fontWeight: 700, color: UI.text, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                Cuenta
              </Typography>
            </Box>

            <Divider sx={{ borderColor: '#edf2f7' }} />

            <List disablePadding sx={{ p: 1 }}>
              {/* Opción Cambiar Contraseña */}
              <ListItemButton
                onClick={handleChangePassword}
                sx={{ borderRadius: '8px', mb: 0.5, '&:hover': { backgroundColor: UI.hover } }}
              >
                <ListItemIcon sx={{ minWidth: 32, color: UI.iconColor }}>
                  <Icon fontSize="small">vpn_key</Icon>
                </ListItemIcon>
                <ListItemText
                  primary="Cambiar contraseña"
                  slotProps={{
                    primary: {
                      sx: {
                        color: UI.text,
                        fontWeight: 500,
                        fontSize: 13,
                      },
                    },
                  }}
                />
              </ListItemButton>

              {/* Opción Cerrar Sesión */}
              <ListItemButton
                onClick={handleLogout}
                sx={{
                  borderRadius: '8px',
                  '&:hover': { backgroundColor: '#fff5f5' }, // Hover sutil rojizo para salida
                  '& .MuiTypography-root': { transition: 'color 0.2s' },
                  '&:hover .MuiTypography-root': { color: '#e53e3e' },
                  '&:hover .MuiBox-root': { color: '#e53e3e' }
                }}
              >
                <ListItemIcon sx={{ minWidth: 32, color: UI.iconColor }}>
                  <Icon fontSize="small" className="transition-colors">exit_to_app</Icon>
                </ListItemIcon>
                <ListItemText
                  primary="Cerrar sesión"
                  slotProps={{
                    primary: {
                      sx: {
                        color: UI.text,
                        fontWeight: 500,
                        fontSize: 13,
                      },
                    },
                  }}
                />
              </ListItemButton>
            </List>

            <Divider sx={{ borderColor: '#edf2f7' }} />

            {/* Footer con versión */}
            <Box sx={{ width: '100%', px: 2, py: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center', backgroundColor: '#f8fafc' }}>
              <Typography sx={{ fontSize: '10px', color: UI.textMuted, fontWeight: 500 }}>
                Pandimed App
              </Typography>
              <Typography variant="caption" style={{ fontWeight: 600, color: UI.textMuted, fontSize: '10px' }}>
                v{packageJson.version}
              </Typography>
            </Box>
          </Popover>
        </React.Fragment>
      )}
    </React.Fragment>
  )
}

export default UserMenu